import { useEffect, useState } from 'react'

/**
 * Installing the app on purpose, rather than waiting to be offered (D-645).
 *
 * <b>The reported problem was "a teacher didn't get the prompt", and the fix is
 * not to chase Chrome's prompt.</b> Another tester on Android saw it, so the
 * browser plainly can offer it - which rules out the tidy explanations and
 * leaves the real one: Chrome's banner appears once, only after its own
 * engagement heuristic, and is suppressed for months by a single dismissal.
 * Nobody can ask for it back. A teacher who swiped it away, or who simply never
 * met the heuristic, has no route to an installed app at all, and on Android
 * that also means no push.
 *
 * So the app keeps the invitation instead. The browser hands it over before
 * showing its own banner; holding it lets us put an <i>Install app</i> button
 * somewhere she can find it whenever she likes.
 *
 * <b>Captured at module load, not in a component.</b> The event fires while the
 * page is still starting, so a listener attached when a screen mounts is
 * attached after the only chance to hear it.
 */

type InstallEvent = Event & { prompt: () => Promise<void> }

let pending: InstallEvent | null = null
const listeners = new Set<(ready: boolean) => void>()

function announce() {
  for (const listener of listeners) listener(pending !== null)
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Chrome shows its own banner unless this is called, and its banner is the
    // one that cannot be asked for again.
    event.preventDefault()
    pending = event as InstallEvent
    announce()
  })

  // Nothing left to offer once it is installed, however it happened.
  window.addEventListener('appinstalled', () => {
    pending = null
    announce()
  })
}

/**
 * Whether the browser has offered, and a way to take it up.
 *
 * `canInstall` is false on iOS - Safari has no such event, which is why the
 * written Share-button steps stay for that platform - and false once installed.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<void> } {
  const [canInstall, setCanInstall] = useState(pending !== null)

  useEffect(() => {
    listeners.add(setCanInstall)
    return () => void listeners.delete(setCanInstall)
  }, [])

  return {
    canInstall,
    install: async () => {
      const event = pending
      if (!event) return

      /*
       * Spent whether she accepts or declines - the browser allows one call per
       * event. Cleared first so a second tap cannot re-fire a used prompt, which
       * throws.
       */
      pending = null
      announce()

      await event.prompt()
    },
  }
}
